import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Input,
  Output,
  Optional,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { addMonths, subMonths } from 'date-fns';
import { ResponsiveService } from 'src/app/common/services/responsive.service';
import { LocalStorageService } from 'src/app/common/services/localStorage.service';
import { BACK } from 'src/defaultImages.js';
import { DateInfo } from '../date-range/interfaces/data-range.interface';
import { DateRangeService } from '../date-range/services/date-range.service';
import { DateService } from 'src/app/common/services/date.service';
import {
  CALENDAR_CONFIG,
  CalendarConfig,
} from '../date-range/services/calendar.service';

export interface SelectedTime {
  hour: number;
  minute: number;
  period: 'AM' | 'PM';
}

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent implements OnInit, OnDestroy {
  @Input() enableTimeSelection: boolean = false;
  @Input() disableDate!: (date: Date) => boolean;
  @Input() defaultDateTimeEpoch!: number;

  @Output() dateTimeSelected = new EventEmitter<number | null>();
  @Output() apply = new EventEmitter<number | null>();
  @Output() cancel = new EventEmitter<void>();

  images = { BACK };
  currentMonth = new Date();
  showTimeInput: boolean = false;
  selectedDate: Date | null = null;
  selectedTime!: SelectedTime;

  private unsubscribe$ = new Subject<void>();
  private subscription: Subscription = new Subscription();

  monthStart!: Date;
  monthDays!: DateInfo[];
  daysOfWeek: string[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  isMobile: boolean = false;
  hasTimeInputError: boolean = false;

  constructor(
    @Optional() @Inject(CALENDAR_CONFIG) private _config: CalendarConfig,
    private _responsiveService: ResponsiveService,
    private _translateService: TranslateService,
    private _localStorage: LocalStorageService,
    private _dateRangeService: DateRangeService,
    private _dateService: DateService
  ) {
    this._translateService.use(this._localStorage.get('language') || 'en_US');

    // Apply configuration if provided
    if (this._config) {
      this.enableTimeSelection = this._config.enableTimeSelection ?? false;
      this.disableDate = this._config.disableDate || this.defaultDateFilter;
      this.initializeDateTime(this._config.defaultDateTimeEpoch);
    }
  }

  ngOnInit(): void {
    this.checkScreenSize();
    this.setTranslations();
    this.updateDays();
    this.initializeDateTime(this.defaultDateTimeEpoch);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.subscription?.unsubscribe();
  }

  selectDate(date: Date): void {
    if (this.isDateDisabled(date)) return;
    this.selectedDate = date;
    this.updateDays();

    this.emitSelection();
  }

  applySelection(): void {
    this.emitSelection();

    const selectedEpoch = this._dateService.convertDateTimeToEpoch({
      date: this.selectedDate,
      time: this.enableTimeSelection ? this.selectedTime : null,
    });

    this.apply.emit(selectedEpoch);
  }

  cancelSelection(): void {
    this.dateTimeSelected.emit();
    this.cancel.emit();
  }

  previousMonth(): void {
    this.currentMonth = subMonths(this.currentMonth, 1);
    this.updateDays();
  }

  nextMonth(): void {
    this.currentMonth = addMonths(this.currentMonth, 1);
    this.updateDays();
  }

  onTimeSelected(selectedTime: SelectedTime): void {
    this.selectedTime = selectedTime;
  }

  onErrorStatus(hasError: boolean): void {
    this.hasTimeInputError = hasError;
  }

  toggleDateTime(): void {
    this.showTimeInput = !this.showTimeInput;
  }

  trackByDayName(index: number, dayName: string): string {
    return dayName;
  }

  trackByDay(index: number, day: { date: Date | null }): Date | null {
    return day?.date;
  }

  private initializeDateTime(epoch?: number): void {
    if (!epoch) return;
    const dateTime = this._dateService.convertEpochToDateTime(epoch);

    this.selectedDate = dateTime.date;
    this.selectedTime = dateTime.time;
  }

  private emitSelection(): void {
    if (this.hasTimeInputError) return;
    const selectedEpoch = this._dateService.convertDateTimeToEpoch({
      date: this.selectedDate,
      time: this.enableTimeSelection ? this.selectedTime : null,
    });

    this.dateTimeSelected.emit(selectedEpoch);
  }

  private setTranslations(): void {
    this.subscription.add(this._translateService.get('DATE_RANGE').subscribe());
  }

  private checkScreenSize(): void {
    const responsiveSubscription = this._responsiveService.mediaBreakpoint$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((breakPoint) => {
        this.isMobile = breakPoint === 'xs';
      });
    this.subscription.add(responsiveSubscription);
  }

  private isDateSelected(date: Date): boolean {
    return (
      !this.isDateDisabled(date) &&
      this._dateRangeService.isSelectedDate(date, this.selectedDate)
    );
  }

  private updateDays(): void {
    const adjacentMonths = this._dateRangeService.getAdjacentMonths(
      this.currentMonth
    );
    this.monthStart = adjacentMonths.firstMonthStart;

    this.monthDays = this._dateRangeService
      .getDaysInMonthWithPadding(this.monthStart)
      .map((date) => ({
        date,
        isDisabled: this.isDateDisabled(date),
        isSelected: this.isDateSelected(date),
        isToday: this._dateRangeService.isToday(date),
      }));
  }

  private defaultDateFilter = (date: Date): boolean => {
    return this._dateService.isFutureDate(date);
  };

  private isDateDisabled(date: Date): boolean {
    return this.disableDate
      ? this.disableDate(date)
      : this.defaultDateFilter(date);
  }
}
